import {
    GLOBAL_SEARCH_MAX_RECENT_IDS,
    GlobalSearchEntityType,
    GlobalSearchFacetGroup,
    GlobalSearchMode,
    GlobalSearchPeekCard,
    GlobalSearchResponse,
    GlobalSearchUpdatedBucket,
    isGlobalSearchEntityType,
} from 'types/globalSearch';
import { portalAPI } from './portalAPI';

/**
 * Everything `GET /v1/search` takes. `q` and `mode` are required; the rest narrow or rank.
 *
 * The list-shaped parameters are repeated once per value (`entityType=MATTER&entityType=INVOICE`),
 * which is what the backend binds — a comma-joined value would depend on Spring's default splitting
 * and would break the first time a value contained a comma.
 */
export interface GlobalSearchParams {
    q: string;
    mode: GlobalSearchMode;
    entityType?: GlobalSearchEntityType[];
    officeId?: number[];
    responsibleId?: number[];
    practiceAreaId?: number[];
    updated?: GlobalSearchUpdatedBucket[];
    /** The offset the previous page returned as `nextCursor`. Omit for the first page. */
    cursor?: number;
    /** `ENTITY_TYPE:id` per recently-opened record — a ranking hint, never an addition. */
    recentIds?: string[];
}

export interface GlobalSearchPeekParams {
    entityType: GlobalSearchEntityType;
    id: number;
}

function appendEach(params: URLSearchParams, name: string, values: readonly (string | number)[] | undefined): void {
    for (const value of values ?? []) {
        params.append(name, String(value));
    }
}

/** Exported for the endpoint's test; the endpoint is the only production caller. */
export function globalSearchQueryString(args: GlobalSearchParams): string {
    const params = new URLSearchParams();
    params.set('q', args.q);
    params.set('mode', args.mode);
    appendEach(params, 'entityType', args.entityType);
    appendEach(params, 'officeId', args.officeId);
    appendEach(params, 'responsibleId', args.responsibleId);
    appendEach(params, 'practiceAreaId', args.practiceAreaId);
    appendEach(params, 'updated', args.updated);
    if (args.cursor !== undefined) {
        params.set('cursor', String(args.cursor));
    }
    // Truncated here rather than at each call site: the backend ignores the overflow anyway, and a
    // browser history long enough to matter would otherwise grow the request line for nothing.
    appendEach(params, 'recentIds', args.recentIds?.slice(0, GLOBAL_SEARCH_MAX_RECENT_IDS));
    return params.toString();
}

/**
 * The ENTITY_TYPE facet names families too, so the rail is narrowed by the same list.
 *
 * Keeping an unknown value here is worse than losing the value it names. The rail draws it, ticking
 * it writes `entityType=<unknown>` to the URL, and the checkbox, the "narrowed by" chip and "clear
 * all" all read that straight back off the URL and report the list as narrowed — while
 * `parseSearchFilters` drops the family before the request goes out, so what comes back is the
 * unnarrowed list minus exactly the family that was asked for. A group left with no values goes with
 * them, since an empty dimension has nothing to offer. The other dimensions carry ids and are not
 * this side's to judge.
 */
function withKnownFacetValues(group: GlobalSearchFacetGroup): GlobalSearchFacetGroup[] {
    if (group.dimension !== 'ENTITY_TYPE') {
        return [group];
    }
    const values = group.values.filter((value) => isGlobalSearchEntityType(value.value));
    return values.length > 0 ? [{ ...group, values }] : [];
}

/**
 * Drops anything belonging to a record family this build does not know.
 *
 * The backend ships ahead of the frontend, so a family added there arrives here before the glyph,
 * the plural name and the detail route that would draw it exist — and a row with none of the three
 * renders as a crash rather than as a row. Dropping it costs that one row; keeping it costs the
 * whole panel. Applied once here so every surface reading this endpoint is covered.
 *
 * Exported for its own test; the endpoint is the only production caller.
 */
export function withKnownFamilies(response: GlobalSearchResponse): GlobalSearchResponse {
    const known = (row: { entityType: string }) => isGlobalSearchEntityType(row.entityType);
    return {
        ...response,
        bestBet: response.bestBet && known(response.bestBet) ? response.bestBet : null,
        sections: response.sections.filter(known).map((section) => ({ ...section, rows: section.rows.filter(known) })),
        rows: response.rows.filter(known),
        facets: response.facets.flatMap(withKnownFacetValues),
    };
}

export const globalSearchApi = portalAPI.injectEndpoints({
    endpoints: (builder) => ({
        globalSearch: builder.query<GlobalSearchResponse, GlobalSearchParams>({
            query: (args) => `/v1/search?${globalSearchQueryString(args)}`,
            transformResponse: withKnownFamilies,
        }),
        /**
         * The hover peek's own fetch, one record at a time. Separate from the search on purpose: it
         * keeps the result list cheap and must never delay the dropdown.
         */
        globalSearchPeek: builder.query<GlobalSearchPeekCard, GlobalSearchPeekParams>({
            query: ({ entityType, id }) => `/v1/search/peek/${entityType}/${id}`,
        }),
    }),
    overrideExisting: false,
});

export const { useGlobalSearchQuery, useLazyGlobalSearchQuery, useGlobalSearchPeekQuery } = globalSearchApi;
